import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { flowsApi, stagesApi } from "@/lib/api";
import { formatDateTime, shortId } from "@/lib/format";
import {
  FlowStatus,
  FlowType,
  type FlowStatus as FlowStatusType,
  type FlowType as FlowTypeType,
  type MaterialItemCreate,
} from "@/lib/types";

export default function FlowsList() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const stageFilter = params.get("stage") ? Number(params.get("stage")) : undefined;
  const status = (params.get("status") as FlowStatusType | null) || undefined;
  const flowType = (params.get("type") as FlowTypeType | null) || undefined;
  const code = params.get("code") || undefined;

  const stages = useQuery({
    queryKey: ["stages", "all"],
    queryFn: () => stagesApi.list({ page_size: 100 }),
  });

  const flows = useQuery({
    queryKey: ["flows", "list", stageFilter, status, flowType, code],
    queryFn: () =>
      flowsApi.list({
        page_size: 100,
        stage_id: stageFilter,
        status,
        flow_type: flowType,
        code,
      }),
  });

  function setFilter(key: string, value: string | undefined) {
    const next = new URLSearchParams(params);
    if (value === undefined || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeader
        eyebrow={t("flows.eyebrow")}
        title={t("flows.title")}
        description={t("flows.description")}
        actions={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            {t("flows.newItem")}
          </button>
        }
      />
      {creating && <CreateModal onClose={() => setCreating(false)} stages={stages.data?.items ?? []} />}

      <section className="card p-4 flex flex-wrap items-center gap-3">
        <Filter
          label={t("flows.filterStage")}
          value={stageFilter !== undefined ? String(stageFilter) : ""}
          onChange={(v) => setFilter("stage", v || undefined)}
          options={[
            { label: t("flows.filterAll"), value: "" },
            ...(stages.data?.items.map((s) => ({ label: s.code, value: String(s.id) })) ?? []),
          ]}
        />
        <Filter
          label={t("flows.filterType")}
          value={flowType ?? ""}
          onChange={(v) => setFilter("type", v || undefined)}
          options={[{ label: t("flows.filterAll"), value: "" }, ...FlowType.map((s) => ({ label: s, value: s }))]}
        />
        <Filter
          label={t("flows.filterStatus")}
          value={status ?? ""}
          onChange={(v) => setFilter("status", v || undefined)}
          options={[{ label: t("flows.filterAll"), value: "" }, ...FlowStatus.map((s) => ({ label: s, value: s }))]}
        />
        <label className="flex items-center gap-2 text-xs">
          <span className="font-mono uppercase tracking-wider text-text-muted">{t("flows.filterCode")}</span>
          <input
            type="text"
            defaultValue={code ?? ""}
            placeholder="FMP, FMP_a…"
            onBlur={(e) => setFilter("code", e.target.value || undefined)}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
            className="px-3 py-1.5 rounded-md bg-surface border border-border text-sm font-mono w-32"
          />
        </label>
      </section>

      {flows.isLoading ? (
        <LoadingState />
      ) : flows.isError ? (
        <ErrorState error={flows.error} />
      ) : flows.data!.items.length === 0 ? (
        <EmptyState title={t("flows.emptyFiltered")} />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-4 py-3">{t("common.id")}</th>
                <th className="px-4 py-3">{t("flows.colCode")}</th>
                <th className="px-4 py-3">{t("flows.colName")}</th>
                <th className="px-4 py-3">{t("flows.colType")}</th>
                <th className="px-4 py-3">{t("flows.colStage")}</th>
                <th className="px-4 py-3">RFID</th>
                <th className="px-4 py-3">{t("flows.colStatus")}</th>
                <th className="px-4 py-3">{t("common.created")}</th>
              </tr>
            </thead>
            <tbody>
              {flows.data!.items.map((i) => (
                <tr key={i.id} className="border-t border-border hover:bg-surface-hover">
                  <td className="px-4 py-3 font-mono text-text-muted">{shortId(i.id)}</td>
                  <td className="px-4 py-3 flex items-center gap-2">
                    <Link to={`/flows/${i.id}`} className="font-mono text-accent-strong hover:underline">
                      {i.code}
                    </Link>
                    <Link
                      to={`/traceability?id=${i.id}`}
                      className="text-xs text-text-muted hover:text-text-primary"
                      title={t("flows.traceabilityTitle")}
                    >
                      ↗
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-text-secondary">{i.name_ru ?? "—"}</td>
                  <td className="px-4 py-3"><span className="pill">{i.flow_type}</span></td>
                  <td className="px-4 py-3 font-mono text-text-muted text-xs">
                    {i.stage_id !== null ? `STAGE_${i.stage_id}` : "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-text-muted text-xs">{i.rfid_tag ?? "—"}</td>
                  <td className="px-4 py-3"><StatusPill kind="flow" value={i.status} /></td>
                  <td className="px-4 py-3 text-text-muted text-xs">{formatDateTime(i.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function CreateModal({
  onClose,
  stages,
}: {
  onClose: () => void;
  stages: Array<{ id: number; code: string; name_ru: string | null }>;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [nameRu, setNameRu] = useState("");
  const [flowType, setFlowType] = useState<FlowTypeType>("INTERNAL");
  const [stageId, setStageId] = useState<number | null>(stages[0]?.id ?? null);
  const [quantity, setQuantity] = useState("1");
  const [unit, setUnit] = useState("шт");
  const [err, setErr] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: (data: MaterialItemCreate) => flowsApi.create(data),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["flows", "list"] });
      onClose();
      nav(`/flows/${created.id}`);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!code.trim()) return setErr(t("flows.errCode"));
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty <= 0) return setErr(t("flows.errQuantity"));
    m.mutate({
      code: code.trim(),
      name_ru: nameRu.trim() || null,
      flow_type: flowType,
      stage_id: stageId,
      quantity: qty,
      unit: unit.trim() || null,
      status: "PENDING",
    });
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="card max-w-lg w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg">{t("flows.modalTitle")}</h2>
        <form onSubmit={submit} className="space-y-3">
          <Field label={t("flows.codeLabel")}>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="FMP-001"
              autoFocus
              autoCapitalize="characters"
              className="w-full px-3 py-2 rounded-md bg-surface border border-border font-mono"
            />
          </Field>
          <Field label={t("flows.nameLabel")}>
            <input
              value={nameRu}
              onChange={(e) => setNameRu(e.target.value)}
              placeholder={t("flows.namePlaceholder")}
              className="w-full px-3 py-2 rounded-md bg-surface border border-border"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("flows.flowTypeLabel")}>
              <select
                value={flowType}
                onChange={(e) => setFlowType(e.target.value as FlowTypeType)}
                className="w-full px-3 py-2 rounded-md bg-surface border border-border font-mono text-sm"
              >
                {FlowType.map((ft) => (
                  <option key={ft} value={ft}>
                    {ft}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("flows.stageLabel")}>
              <select
                value={stageId ?? ""}
                onChange={(e) => setStageId(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 rounded-md bg-surface border border-border font-mono text-sm"
              >
                <option value="">{t("flows.stageNotSet")}</option>
                {stages.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("flows.quantityLabel")}>
              <input
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                type="number"
                step="any"
                min="0"
                className="w-full px-3 py-2 rounded-md bg-surface border border-border tabular-nums"
              />
            </Field>
            <Field label={t("flows.unitLabel")}>
              <input
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="шт / кг / м²"
                className="w-full px-3 py-2 rounded-md bg-surface border border-border"
              />
            </Field>
          </div>
          {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" className="btn" onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={m.isPending}>
              {m.isPending ? t("common.creating") : t("common.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Filter({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { label: string; value: string }[] }) {
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-1.5 rounded-md bg-surface border border-border text-sm"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
